import React from 'react';
import { ThemeProvider } from '@platform-ui/zds-next';
import { createTheme, ThemeOptions } from '@mui/material';
import { designTokens } from '@platform-ui/design-system';
import { useDarkMode } from './hooks/useDarkMode';

const getThemeOptions = (mode: 'light' | 'dark'): ThemeOptions => {
  const isLightTheme = mode === 'light';

  const result: ThemeOptions = {
    typography: {
      fontFamily: designTokens.fonts.fontFamily,
    },
    components: {
      MuiAccordion: {
        styleOverrides: {
          root: {
            '&:first-of-type': {
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
            },
            '&:last-of-type': {
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            },
            '&.Mui-disabled': {
              backgroundColor: designTokens.colors.navy700,
            },
            '&:before, &.Mui-expanded:before': {
              backgroundColor: `${designTokens.colors.navy800} !important`,
            },
            '&.Mui-expanded + &': {
              '&:before': {
                display: 'block',
              },
            },
            margin: '0 !important',
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            '&.Mui-expanded': {
              boxShadow: '0 2px 2px 0 ' + designTokens.colors.lightEmphasisLow,
            },
            '&:hover, &:focus, &.Mui-expanded': {
              color: designTokens.colors.white,
              '& path': {
                fill: designTokens.colors.darkEmphasisHigh,
              },
              '& rect': {
                stroke: designTokens.colors.darkEmphasisHigh,
              },
            },
            '&:focus': {
              background: `linear-gradient(0deg,
                ${designTokens.colors.lightContextExtraHigh},
                ${designTokens.colors.lightContextExtraHigh}),
                ${designTokens.colors.navy600}`,
            },
            '&:hover': {
              background: designTokens.colors.navy800,
            },
            background: designTokens.colors.navy700,
            color: designTokens.colors.darkEmphasisExtraMedium,
            minHeight: '56px !important',
            paddingLeft: 0,
          },
          content: {
            alignItems: 'center',
            margin: '0 !important',
          },
          expandIconWrapper: {
            color: designTokens.colors.darkEmphasisExtraMedium,
            '&:hover, &:focus, &.Mui-expanded': {
              color: designTokens.colors.white,
            },
          },
        },
      },
      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            background: `linear-gradient(0deg, 
              ${designTokens.colors.lightContextHigh}, 
              ${designTokens.colors.lightContextHigh}), 
              ${designTokens.colors.navy600}`,
            padding: '16px 0',
            flexDirection: 'column',
          },
        },
      },
      MuiInput: {
        defaultProps: {
          disableUnderline: true,
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: isLightTheme
              ? designTokens.colors.lightLink
              : designTokens.colors.darkLink,
          },
        },
        defaultProps: {
          underline: 'none',
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            backgroundColor: designTokens.colors.white,
          },

          action: {
            marginTop: '3px',
            '& .MuiButtonBase-root, &.MuiIconButton-root': {
              marginTop: '-6px',
            },
          },
        },
      },
    },
    palette: {
      mode,
      background: {
        default: isLightTheme
          ? designTokens.colors.lightPageBackground
          : designTokens.colors.darkPageBackground,
        paper: isLightTheme
          ? designTokens.colors.lightMainContainer
          : designTokens.colors.darkMainContainer,
      },
    },
  };
  return result;
};

export const lightTheme = createTheme(getThemeOptions('light'));
export const darkTheme = createTheme(getThemeOptions('dark'));

export const Theme: React.FC = ({ children }) => {
  const [darkMode] = useDarkMode();

  return (
    <ThemeProvider theme={darkMode ? darkTheme : lightTheme}>
      {children}
    </ThemeProvider>
  );
};
